import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxTime, OnyxTimeRange } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxFormGroupComponent,
  OnyxIconButtonComponent,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
  onyxTimeRangeValidator,
} from '@onyx/angular';
import { map } from 'rxjs';
import { CommonHelper } from '../../../../../../../common/helpers/common.helper';
import { SuggestionHelper } from '../../../../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import type { PointOfInterest } from '../../../../../../management-panel/points-of-interest/common/interfaces/point-of-interest';
import type { OrderPointCategory } from '../../../../../common/enums/order-point-category';
import { OrderTimeWindowType } from '../../../../../common/enums/order-time-window-type';
import { orderOpeningHoursDateValidator } from '../../../../../common/validators/order-opening-hours-date.validator';
import { orderTimeWindowDurationValidator } from '../../../../../common/validators/order-time-window-duration.validator';

export type OrderPointTimeWindowFormGroup = ReturnType<
  typeof OrderPointTimeWindowFormComponent.buildForm
>;

@Component({
  selector: 'app-order-point-time-window-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    OnyxTimepickerComponent,
    OnyxDatepickerComponent,
    OnyxSuggestionsComponent,
    OnyxIconButtonComponent,
    OnyxButtonComponent,
    OnyxFormGroupComponent,
  ],
  templateUrl: './order-point-time-window-form.component.html',
  styleUrl: './order-point-time-window-form.component.scss',
})
export class OrderPointTimeWindowFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);

  public readonly form = input.required<OrderPointTimeWindowFormGroup>();
  public readonly category = input.required<
    OrderPointCategory.LOADING | OrderPointCategory.UNLOADING
  >();
  public readonly serviceTime = input.required<number | null>();
  public readonly first = input.required<boolean>();
  public readonly last = input.required<boolean>();
  public readonly type = input.required<OrderTimeWindowType>();
  public readonly disabled = input(false);
  public readonly pointOfInterest = input.required<PointOfInterest | null>();
  public readonly isCompleted = input.required<boolean>();

  public readonly add = output<void>();
  public readonly remove = output<void>();

  protected readonly I18N = 'orders.orderForm.points.timeWindow';
  protected readonly TIME_RANGE_SUGGESTIONS =
    SuggestionHelper.getTimeRangeSuggestions();
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
  );

  protected readonly OrderTimeWindowType = OrderTimeWindowType;

  protected isOverlapping!: Signal<boolean>;

  protected timeWindowTimeLabel = computed(
    () =>
      ({
        [OrderTimeWindowType.INTERVAL]: 'labels.interval',
        [OrderTimeWindowType.FIX]: 'Fix',
        [OrderTimeWindowType.OPENING_HOURS]: 'labels.openingHours',
      })[this.type()],
  );

  public ngOnInit(): void {
    const form = this.form();
    const controls = form.controls;

    this.isOverlapping = toSignal(
      this.form().statusChanges.pipe(
        map(() => this.form().hasError('orderPointTimeWindowOverlap')),
      ),
      { initialValue: false, injector: this.injector },
    );

    const openingHoursDateValidator = orderOpeningHoursDateValidator(
      this.category,
      this.serviceTime,
      controls.timeRange,
    );

    effect(
      () => {
        if (form.disabled) return;
        const type = this.type();

        ValidationHelper.toggleControls(controls.time, {
          enabled: type === OrderTimeWindowType.FIX,
          reset: true,
        });
        ValidationHelper.toggleControls(controls.timeRange, {
          enabled: type !== OrderTimeWindowType.FIX,
          reset: true,
        });
        this.form().updateValueAndValidity();

        if (type === OrderTimeWindowType.OPENING_HOURS) {
          ValidationHelper.disableControls(controls.timeRange, { reset: true });
          controls.date.addValidators(openingHoursDateValidator);
        } else {
          controls.date.removeValidators(openingHoursDateValidator);
        }

        controls.date.updateValueAndValidity();
      },
      { injector: this.injector },
    );

    const dateValue = toSignal(controls.date.valueChanges, {
      initialValue: controls.date.value,
      injector: this.injector,
    });
    effect(
      () => {
        if (this.type() !== OrderTimeWindowType.OPENING_HOURS) return;

        const [pointOfInterest, date] = [this.pointOfInterest(), dateValue()];

        if (!date || (!this.isCompleted() && !pointOfInterest)) {
          controls.timeRange.setValue(null);
          controls.date.updateValueAndValidity();
          return;
        }

        const openingHours = CommonHelper.getOpeningHours(
          date,
          pointOfInterest?.businessHours ?? null,
        );
        if (!this.isCompleted()) controls.timeRange.setValue(openingHours);

        controls.date.updateValueAndValidity();
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const [category, serviceTime] = [this.category(), this.serviceTime()];
        controls.timeRange.setValidators([
          Validators.required,
          onyxTimeRangeValidator,
          orderTimeWindowDurationValidator(category, serviceTime),
        ]);
        controls.timeRange.updateValueAndValidity();

        if (this.type() !== OrderTimeWindowType.OPENING_HOURS) return;
        controls.date.updateValueAndValidity();
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group(
      {
        date: fb.control<string | null>(null, [Validators.required]),
        time: fb.control<OnyxTime | null>(null, [Validators.required]),
        timeRange: fb.control<OnyxTimeRange | null>(null, [
          Validators.required,
          onyxTimeRangeValidator,
        ]),
      },
      { updateOn: 'blur' },
    );
  }
}
